import * as routes from '../../routes'
import React, { ReactNode, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'

import LoadingAnimation from './LoadingAnimation'
import LoadingFailed from './LoadingFailed'
import { RootState } from '../../store'
import { fetchEvents } from '../../store/events/events'
import { fetchInstitution } from '../../store/institution/institution'
import { fetchProgram } from '../../store/program/program'
import { addToast, setData, fetchLiveMessages } from '../../store/misc/misc'
import { useLocation, useNavigate } from 'react-router-dom'
import { ToastType } from '../../store/misc/interfaces'
import { resetAll } from '../../store/actions'

const CeruleanTheme = React.lazy(() => import('../themes/Cerulean/cerulean'))
const BeruleanTheme = React.lazy(() => import('../themes/Berulean/berulean'))
const CosmoTheme = React.lazy(() => import('../themes/Cosmo/cosmo'))
const FlatlyTheme = React.lazy(() => import('../themes/Flatly/flatly'))
const JournalTheme = React.lazy(() => import('../themes/Journal/journal'))
const SimplexTheme = React.lazy(() => import('../themes/Simplex/simplex'))
const SpacelabTheme = React.lazy(() => import('../themes/Spacelab/spacelab'))
const UnitedTheme = React.lazy(() => import('../themes/United/united'))
const YetiTheme = React.lazy(() => import('../themes/Yeti/yeti'))
const RedbaronTheme = React.lazy(() => import('../themes/Redbaron/redbaron'))
const ReadableTheme = React.lazy(() => import('../themes/Readable/readable'))

const ThemeSelector = ({ theme, children }: { theme: string, children: ReactNode }) => {
  return (
    <>
      <React.Suspense fallback={<></>}>
        {theme === 'cerulean' && <CeruleanTheme />}
        {theme === 'berulean' && <BeruleanTheme />}
        {theme === 'cosmo' && <CosmoTheme />}
        {theme === 'flatly' && <FlatlyTheme />}
        {theme === 'journal' && <JournalTheme />}
        {theme === 'simplex' && <SimplexTheme />}
        {theme === 'spacelab' && <SpacelabTheme />}
        {theme === 'united' && <UnitedTheme />}
        {theme === 'yeti' && <YetiTheme />}
        {theme === 'readable' && <ReadableTheme />}
        {theme === 'redbaron' && <RedbaronTheme />}
      </React.Suspense>
      {children}
    </>
  )
}

const InitialData = (props: {
  baseUrl: string
  programId: number
  event_id: string
  participant_token?: string
  family_token?: string
  children?: any
  location: any
  isRestoredData: boolean
}) => {
  const { baseUrl, programId, event_id, participant_token, isRestoredData, family_token } = props
  const dispatch = useAppDispatch()

  const { loaded: programLoaded, error: programFailed } = useAppSelector(
    (state: RootState) => state.program,
  )
  const { loaded: eventsLoaded, error: eventsFailed } = useAppSelector(
    (state: RootState) => state.events,
  )

  const {
    data: { theme },
  } = useAppSelector((state: RootState) => state.institution)

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if(family_token || participant_token) {
      dispatch(resetAll())
    }
  }, [dispatch, family_token, participant_token])

  useEffect(() => {
    dispatch(setData({ baseUrl: baseUrl, programId: programId }))
    dispatch(fetchInstitution())
    dispatch(fetchProgram())
    dispatch(fetchLiveMessages())

    if (isRestoredData) {
      if(family_token) {
        dispatch(addToast({
          body: `Persönlichen Daten und Anmeldungen des Familienmitglieds wurden übernommen!`,
          type: ToastType.Success,
          title: 'Anmeldung eines Familienmitglieds',
        }))
      } else if (participant_token) {
        // do nothing, it's shown from somewhere else
      }
      else {
        dispatch(addToast({
          body: `Gespeicherte Daten wurden geladen!`,
          type: ToastType.Success,
          title: 'Hinweis',
        }))
      }
    }

    // If the data is restored and event_id exists, navigate to the event
    if (event_id) {
      navigate(routes.EVENTS.path + '/' + event_id)
    } else {
      navigate(routes.EVENTS.path + location.hash)
    }

  }, [dispatch, baseUrl, programId, event_id, isRestoredData])

  useEffect(() => {
    if (programLoaded) {
      dispatch(fetchEvents(navigate, participant_token || family_token))
    }
    // exclude navigate here, otherwise fetch will be triggered on each navigation!
  }, [dispatch, programLoaded, programId, participant_token, family_token])

  if (programLoaded && eventsLoaded) {
    return (
      <>
        <ThemeSelector theme={theme}>{props.children}</ThemeSelector>
      </>
    )
  } else if (programFailed) {
    return <LoadingFailed text="Das Programm konnte nicht geladen werden." additionalInfo="Es existiert nicht oder ist noch nicht aktiv.">
      <p>Zurück zur <a href='/'>Startseite</a></p>
    </LoadingFailed>
  } else if (eventsFailed) {
    return (
      <LoadingFailed text='Veranstaltungen konnten nicht geladen werden.' additionalInfo="Probieren Sie es später nochmals.">
        <p>Zurück zur <a href='/'>Startseite</a></p>
      </LoadingFailed>
    )
  } else {
    return <LoadingAnimation text='Programm und Veranstaltungen werden geladen...' />
  }
}

export default InitialData